import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { graphql } from '@apollo/client/react/hoc';
import * as Sentry from '@sentry/react';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';

import { withError } from '@/components/errorBoundary/errorBoundary';
import { Footer, Navbar } from '@/components/layouts';
import { KanbanHeader } from '@/components/layouts/kanban';
import { history } from '@/components/root';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { UserDashboardHeader } from '@/components/userDashboard/userDashboardHeader/userDashboardHeader';
import { env } from '@/constant/env';
import { TOOLTIP_PORTAL } from '@/constant/htmlIds';
import { ROUTE_LOGIN } from '@/constant/routes';
import { useUser } from '@/hooks/useUser';
import { userDashboardFragments } from './fragments';
import GroupsTaskList from './groupsTaskList';
import { WorkflowCardsDashboard } from './WorkflowCardsDashboard';
import {
  WorkflowsList,
  fragments as workflowsListFragments
} from './workflowsList/workflowsList';

// some constants specific to dashboard
const LOCAL_STORAGE_KEY = 'userDashboardConfig';

export const getUserDisplayConfig = () => {
  try {
    const localStorageDisplayConfigStr =
      localStorage.getItem(LOCAL_STORAGE_KEY);

    if (localStorageDisplayConfigStr) {
      return JSON.parse(localStorageDisplayConfigStr);
    }

    return null;
  } catch {
    return null;
  }
};

export const saveUserDisplayConfig = cfg => {
  const lsKey = LOCAL_STORAGE_KEY;
  if (!cfg) {
    localStorage.removeItem(lsKey);
    return;
  }
  localStorage.setItem(lsKey, JSON.stringify(cfg));
};

export const UserDashboard = React.memo(({ dashboardUserQuery }) => {
  // set doc title at mount
  useEffect(() => {
    document.title = 'Trace';
  }, []);

  // state init
  const [userDisplayConfig, setUserDisplayConfig] = useState(
    getUserDisplayConfig() || {}
  );
  const { collapsedHeader, groupsTaskList: groupsTaskListConfig } =
    userDisplayConfig;

  // actions logged in userDisplayConfig
  // overlay a partial config onto the main config
  const updateUserDisplayConfig = useCallback(
    overlayConfig => {
      // update the user display config and store it
      const newUserDisplayConfig = {
        ...userDisplayConfig,
        ...overlayConfig
      };
      setUserDisplayConfig(newUserDisplayConfig);
      saveUserDisplayConfig(newUserDisplayConfig);
    },
    [userDisplayConfig]
  );

  // set collapsed dashboard header
  const setCollapsedHeader = useCallback(
    newCollapsedHeader => {
      updateUserDisplayConfig({ collapsedHeader: newCollapsedHeader });
    },
    [updateUserDisplayConfig]
  );

  // set groups task list config
  const setGroupsTaskListConfig = useCallback(
    newGroupsTaskListConfig => {
      updateUserDisplayConfig({ groupsTaskList: newGroupsTaskListConfig });
    },
    [updateUserDisplayConfig]
  );

  // main header memo
  const header = useMemo(
    () => (
      <KanbanHeader>
        <Navbar />
      </KanbanHeader>
    ),
    []
  );

  const { loading: dashboardLoading, workflows } = dashboardUserQuery;

  const { user, isPrivileged, loading: userLoading } = useUser();

  useEffect(() => {
    const isLoading = dashboardLoading || userLoading;
    if (!(isLoading || (workflows && user))) {
      Sentry.setContext('workflows', {
        data: JSON.stringify(workflows)
      });
      Sentry.captureMessage('User dashboard without user', 'error');
      history.push(ROUTE_LOGIN);
    }
  }, [dashboardLoading, userLoading]);

  return (
    <>
      <div id={TOOLTIP_PORTAL} />
      {header}
      <div className="fixed top-navbar h-[calc(100%-var(--spacing-navbar))] w-full overflow-x-auto overflow-y-scroll bg-background">
        {/* can load infinitly be careful */}
        {dashboardLoading || !user || !workflows ? (
          <TraceIconSpinner />
        ) : (
          <div className="relative flex flex-col flex-nowrap p-4">
            <UserDashboardHeader
              className="m-2 rounded-2xl border border-border"
              collapsed={collapsedHeader}
              setCollapsed={setCollapsedHeader}
            />
            <WorkflowsList
              className="m-2 rounded-2xl border border-border"
              isPrivileged={isPrivileged}
              userId={user.rowId}
              workflows={workflows}
            />
            <GroupsTaskList
              className="m-2 rounded-2xl border border-border"
              config={groupsTaskListConfig}
              setConfig={setGroupsTaskListConfig}
              workflows={workflows.nodes}
            />
            <Footer />
          </div>
        )}
      </div>
    </>
  );
});

UserDashboard.propTypes = {
  dashboardUserQuery: PropTypes.object.isRequired
};

const queries = {
  dashboardUserQuery: gql`
    query dashboardUserQuery {
      ...DashboardUserFragment
      ...WorkflowsFragment
    }
    ${userDashboardFragments.dashboardUser}
    ${workflowsListFragments.workflows}
  `
};

const LegacyUserDashboard = compose(
  graphql(queries.dashboardUserQuery, {
    name: 'dashboardUserQuery'
  }),
  withError
)(UserDashboard);

const UserDashboardSwitch = props =>
  env.FEATURE_FLAGS.USE_WORKFLOW_CARDS ? (
    <WorkflowCardsDashboard />
  ) : (
    <LegacyUserDashboard {...props} />
  );

export default UserDashboardSwitch;
